import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { debounce } from "lodash";
import {
	Avatar,
	Button,
	Col,
	Form,
	Input,
	Modal,
	PageHeader,
	Row,
	Select,
	Table,
	Tooltip,
} from "antd";
import moment from "moment-jalaali";

import { CheckPermission, GetUrlParams, ObjectToURL } from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import { Modals, Permissions } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";

import FormModal from "modals/PlatformForm";

const Component = ({ onSetRoute, routeName, parentName, permissions }) => {
	const initialState = { data: [], images: [], pagination: {} };
	const tableColumns = [
		{
			title: Resources.row,
			dataIndex: "index",
			className: "center-cell",
			width: "60px",
			render: (value, record, index) =>
				(pagination.current - 1) * pagination.pageSize + index + 1,
		},

		{
			title: Resources.title,
			dataIndex: "title",
		},
		{
			title: Resources.os,
			dataIndex: "os",
			className: "center-cell",
		},
		{
			title: Resources.image,
			dataIndex: "cover",
			className: "image-cell center-cell",
			width: "80px",
			render: (value) => (
				<Tooltip
					title={
						<Avatar
							src={value.url}
							shape="square"
							size={200}
						/>
					}
				>
					<Avatar
						src={value.url}
						shape="square"
					/>
				</Tooltip>
			),
		},
		{
			title: Resources.status,
			dataIndex: "status",
			className: "center-cell color-cell",
			width: "120px",
			render: (value, record) =>
				permissions.status ? (
					<span
						className={`btn ${value ? "green" : "red"}`}
						onClick={() => handleStatus(record)}
					>
						{value ? Resources.active : Resources.inactive}
					</span>
				) : (
					<span className={value ? "green" : "red"}>
						{value ? Resources.active : Resources.inactive}
					</span>
				),
		},
		{
			title: `${Resources.date} ${Resources.record}`,
			dataIndex: "createdAt",
			className: "center-cell desc-cell ltr-cell",
			width: "100px",
			render: (value) => {
				const [date, time] = moment(value)
					.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
					.split(" ");

				return (
					<>
						<span className="name">{date}</span>
						<small className="desc">{time}</small>
					</>
				);
			},
		},
		{
			title: Resources.action,
			dataIndex: "id",
			className: "center-cell",
			width: "120px",
			render: (value, record) => (
				<>
					{permissions.update && (
						<Tooltip title={Resources.edit}>
							<Button
								type="link"
								icon={<i className="fa fa-pencil-alt" />}
								onClick={() => handleForm(record)}
							/>
						</Tooltip>
					)}
					{permissions.delete && (
						<Tooltip title={Resources.delete}>
							<Button
								type="link"
								icon={<i className="fa fa-trash-alt" />}
								onClick={() => handleDelete(value)}
							/>
						</Tooltip>
					)}
				</>
			),
		},
	];
	const [state, setState] = useState(initialState);
	const { data, loading, pagination, selected, images } = state;
	const { pathname, search } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const os = [
		{ value: "ios" },
		{ value: "windows" },
		{ value: "mac" },
		{ value: "android" },
		{ value: "linux" },
	];

	const fetchData = () => {
		const params = GetUrlParams(search);

		form.resetFields();
		form.setFieldsValue(params);

		setState((prevState) => ({ ...prevState, loading: true }));

		Promise.all([
			API.platform.list({
				...params,
				pageSize: process.env.REACT_APP_PAGE_SIZE,
			}),
			API.gallery.list({ paginate: false, section: "platform" }),
		])

			.then(
				([
					{
						data,
						meta: { currentPage, perPage, total },
					},
					{ data: images },
				]) => {
					setState((prevState) => ({
						...prevState,
						data,
						images: images.map(({ id, title }) => ({
							label: title,
							value: id,
						})),
						pagination: {
							current: currentPage,
							pageSize: perPage,
							total,
							showSizeChanger: false,
						},
						loading: false,
					}));
				}
			)
			.catch(() => {
				setState((prevState) => ({ ...prevState, loading: false }));
			});
	};

	const handleForm = (selected, reload) => {
		if (reload) {
			fetchData();
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(
				`${pathname}${search}#${selected ? Modals.EDIT : Modals.CREATE}`
			);
		}
	};

	const handleDelete = (id) => {
		Modal.confirm({
			title: Resources.deleteConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.platform
					.delete(id)
					.then(() => {
						setState((prevState) => ({ ...prevState, loading: false }));

						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleStatus = (selected) => {
		Modal.confirm({
			title: Resources.statusConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.platform
					.status(selected.id)
					.then(() => {
						setState((prevState) => ({ ...prevState, loading: false }));

						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleTable = ({ current }) => {
		const params = GetUrlParams(search);
		const query = ObjectToURL({ ...params, page: current });

		navigate(`${pathname}?${query}`);
	};

	const handleFilter = debounce(() => {
		form.validateFields().then((values) => {
			const search = ObjectToURL({ page: 1, ...values });

			navigate(`${pathname}?${search}`);
		});
	}, 1000);

	const componentDidUpdate = () => {
		fetchData();
	};

	const componentDidMount = () => {
		onSetRoute({ openKey: parentName, selectedKey: routeName });
	};

	useEffect(componentDidUpdate, [search]);
	useEffect(componentDidMount, []);

	return (
		<>
			<PageHeader
				title={`${Resources.platform} ${Resources.ha}`}
				extra={
					permissions.create && (
						<Button
							type="primary"
							onClick={() => handleForm()}
							loading={loading}
						>
							{Resources.add}
						</Button>
					)
				}
			/>
			<Form
				layout="vertical"
				form={form}
			>
				<Row gutter={24}>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="search">
							<Input
								suffix={<i className="fa fa-search" />}
								onChange={handleFilter}
								placeholder={Resources.search}
								allowClear
								autoFocus
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="os">
							<Select
								suffix={<i className="fa fa-search" />}
								onChange={handleFilter}
								placeholder={Resources.os}
								options={os}
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="status">
							<Select
								suffix={<i className="fa fa-search" />}
								onChange={handleFilter}
								placeholder={Resources.status}
								options={[
									{ value: "true", label: Resources.active },
									{ value: "false", label: Resources.inactive },
								]}
								allowClear
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Table
				columns={tableColumns}
				dataSource={data}
				loading={loading}
				onChange={handleTable}
				pagination={pagination}
				scroll={{ x: 930 }}
				rowKey="id"
			/>
			<FormModal
				data={selected}
				onUpdate={handleForm}
				options={{ os, images }}
			/>
		</>
	);
};

export default connect(
	(state) => {
		return {
			permissions: {
				create: CheckPermission(
					state.user.permissions,
					Permissions.PLATFORM_CREATE
				),
				delete: CheckPermission(
					state.user.permissions,
					Permissions.PLATFORM_DELETE
				),
				status: CheckPermission(
					state.user.permissions,
					Permissions.PLATFORM_STATUS
				),
				update: CheckPermission(
					state.user.permissions,
					Permissions.PLATFORM_UPDATE
				),
			},
		};
	},
	(dispatch) => {
		return {
			onSetRoute: (data) => dispatch(setRoute(data)),
		};
	}
)(Component);
